/**
 * A table column's header filter, as data (KPR-1639, KPE-051 ruling i06): which
 * condition a column's header shows and edits, and the condition it writes.
 *
 * A HEADER FILTER IS A CONDITION IN THE ONE FILTER, never a layer of its own.
 * It reads the page's `?q=` and writes `?q=`, through the same writers every
 * other door uses: the value nodes are kqlBuilder's `makeValueNode` (what the
 * filter builder's rows call), and the write is utils/filterValues'
 * `applyConditionsToQuery` with `replacePath` (what a quick filter chip
 * calls) or `clearConditionsOnPath`. Nothing here parses or serializes text of
 * its own, so the KQL a header writes is the KQL the builder writes for the
 * same condition.
 *
 * A column's condition is every TOP-LEVEL condition on its element (joined to
 * the rest by AND), in any of its spellings: by id (`#k7f3q2xa`, how the
 * builder writes it), by name (text saved before ids), or as an element group
 * holding its id ("any of these cards"). The header edits it only when it can
 * show it whole. When the element appears under an `or`, a `not` or a nested
 * group, or at the top in a shape the header has no control for (`!=` on a
 * choice, `>` on a number, a repeating group's `has`), or the query does not
 * parse, the header says "Set in the filter builder" and opens the builder:
 * it never rewrites a filter it cannot show.
 *
 * The kinds a header filters:
 *   text     short, long and rich text, email, URL, phone, location:
 *            Contains (the default) · Is · Is empty · Is not empty
 *   choice   a checklist of the values, with (Empty); several ticked is
 *            "any of" (`in`). (Empty) is never ticked beside a value: "any of
 *            A or empty" is two conditions joined by `or`, which no chip shows.
 *   boolean  Yes · No · Empty, in the element's own words
 *   range    number, integer and decimal, rating, a me-too count, currency,
 *            measurement: a lowest and a highest (`>=`, `<=`) · Is empty
 *   file     Has a file · Has none
 *   entries  a repeating group: Has entries · Has none
 * and, since KPR-1712:
 *   date     date and date-time, Created and Updated: Today · Last 7 days ·
 *            This month · Overdue (a card's own dates only) · a custom range
 *            (utils/headerFilterDates.js)
 *   user     a person element and Created by: the account picker, Me
 *            included; several picked is "any of" (`in`)
 *   tag      the item's tags: the tag picker; several is "any of"
 *   lookup   an item lookup that names its card: the item picker; several is
 *            "any of" (`in`, which the builder offers on a lookup since
 *            KPR-1712)
 * One pick writes `=` and several write `in`, as a choice's checklist does.
 *
 * The item's own columns filter on the item's own fields, spelled as the
 * builder spells them: Created on `created`, Updated on `updated`, Created by
 * on `created.by`, Tags on `tags`.
 */
import { MAX_QUERY_LENGTH, isElementIdSegment, isHexId, isSystemField } from './kql';
import {
	CREATOR_FILTER_FIELD,
	ENTRIES_KIND,
	elementGroupIds,
	fieldKindOf,
	findField,
	isElementGroupSegment,
	isNullaryOperator,
	makeValueNode,
	parseForBuilder,
	pathKey,
	rawValueOf,
	topLevelConditionNodes,
} from './kqlBuilder';
import { applyConditionsToQuery, clearConditionsOnPath } from './filterValues';
import { TAG_FIELD_KEY } from './tagNames';
import { dateDraftConditions, dateDraftProblem, emptyDateDraft, readDateDraft } from './headerFilterDates';
import { ME_VALUE } from './userElements';

export const HEADER_KINDS = ['text', 'choice', 'boolean', 'range', 'file', 'entries', 'date', 'user', 'tag', 'lookup'];

// The kinds a header picks values for: one is `=`, several are `in`.
const PICKED_KINDS = new Set(['user', 'tag', 'lookup']);

// The item's own columns (components/items/ItemsTable keys) and the filter
// field each one filters on. The primary date, the source's date and the
// sub-collection have no filter field.
const SYSTEM_COLUMN_FIELDS = {
	'sys:createdAt': 'created',
	created: 'created',
	'sys:updatedAt': 'updated',
	updated: 'updated',
	'sys:owner': CREATOR_FILTER_FIELD.key,
	'sys:tags': TAG_FIELD_KEY,
};
const SYSTEM_KINDS = { created: 'date', updated: 'date', [CREATOR_FILTER_FIELD.key]: 'user', [TAG_FIELD_KEY]: 'tag' };

// The types whose header is a text box: what the builder reads as text, by
// name, so a type the platform adds later is not filtered as text by default.
const TEXT_TYPES = new Set(['text-small', 'text-large', 'rich-text', 'email', 'url', 'phone', 'location']);

/** The operators of a text header, in the order it offers them. */
export const TEXT_OPS = ['contains', 'eq', 'empty', 'nempty'];
export const TEXT_OP_LABELS = { contains: 'Contains', eq: 'Is', empty: 'Is empty', nempty: 'Is not empty' };

/** The kind of header filter a field gets, or null when its header has none yet. */
export function headerFilterKind(field) {
	if (!field || field.unknown || field.facet) return null;
	if (field.system) {
		// Tags where they decide who sees what are not this page's.
		if (field.key === TAG_FIELD_KEY && field.options?.mode === 'access') return null;
		return SYSTEM_KINDS[field.key] || null;
	}
	if (TEXT_TYPES.has(field.dataType)) return 'text';
	switch (fieldKindOf(field)) {
		// A `time` holds a clock, which the builder asks with a clock: its own.
		case 'date': return field.dataType === 'date' || field.dataType === 'date-time' ? 'date' : null;
		case 'user': return 'user';
		case 'tag': return 'tag';
		// Only a lookup that names its card has a picker to hold its items.
		case 'lookup': return field.options?.lookupCardId ? 'lookup' : null;
		case 'choice': return 'choice';
		case 'boolean': return 'boolean';
		case 'number':
		case 'measurement':
		case 'currency':
			return 'range';
		case 'file': return 'file';
		case ENTRIES_KIND: return 'entries';
		default: return null;
	}
}

/**
 * The filter behind one column, or null when the column is not an element's
 * (the title, the item's own fields, the type: their headers have no filter).
 * `key` is the column's antd key (`el:<name>`, or one of the item's own
 * columns, `sys:createdAt` and the rest); `defsByName` the card's elements;
 * `fields` the page's filter vocabulary.
 *
 * `{ key, name, field, kind, path, forceElement, aliases, overdue }`. `field` is null
 * when the vocabulary does not hold the element (gone, or not this reader's
 * to filter on); `kind` is null for a type the header does not filter (a time
 * of day, a lookup that names no card).
 */
export function columnFilterOf(key, { defsByName = null, fields = [] } = {}) {
	if (typeof key !== 'string') return null;
	const systemKey = SYSTEM_COLUMN_FIELDS[key];
	if (systemKey) return systemColumnFilterOf(key, systemKey, fields);
	if (!key.startsWith('el:')) return null;
	const name = key.slice(3);
	const def = defsByName?.get(name);
	if (!def) return null;
	const forceName = isSystemField({ path: [name] });
	const nameKey = pathKey([name], forceName);
	// The element by its id, as the builder offers it; by name only where the
	// definition carries none.
	const elementId = typeof def.id === 'string' && isElementIdSegment(`#${def.id}`) ? def.id : null;
	const path = elementId ? [`#${elementId}`] : [name];
	const forceElement = elementId ? false : forceName;
	const found = findField(fields, path, forceElement);
	const field = found && !found.unknown ? found : null;
	return {
		key,
		name,
		elementId,
		field,
		kind: headerFilterKind(field),
		path,
		forceElement,
		// Every spelling of the same element: its id and its name.
		aliases: [...new Set([pathKey(path, forceElement), nameKey])],
		// A card's own date may be late: Overdue is offered on every one, as
		// the builder offers it (the owner's ruling, KPR-1712 Q1).
		overdue: true,
	};
}

/**
 * One of the item's own columns: its field in the vocabulary (absent when the
 * page built none), spelled as the builder spells it. Created and Updated are
 * keepr's stamps, never after today, so they offer no Overdue.
 */
function systemColumnFilterOf(key, fieldKey, fields) {
	const found = (fields || []).find(candidate => candidate?.key === fieldKey && candidate.system && !candidate.facet);
	const field = found || null;
	const path = field ? [...field.path] : fieldKey.split('.');
	return {
		key,
		name: fieldKey,
		elementId: null,
		field,
		kind: headerFilterKind(field),
		path,
		forceElement: false,
		aliases: [pathKey(path, false)],
		overdue: false,
	};
}

/** Is this condition on the column, in any of its spellings? */
function onColumn(node, column) {
	if (!node || node.op !== 'cmp' || node.facet || !Array.isArray(node.path)) return false;
	if (column.aliases.includes(pathKey(node.path, !!node.forceElement))) return true;
	return Boolean(column.elementId) && node.path.length === 1 && isElementGroupSegment(node.path[0])
		&& elementGroupIds(node.path[0]).includes(column.elementId);
}

/** Does the column appear anywhere inside this node (an `or`, a `not`, a nested group)? */
function mentions(node, column) {
	if (!node || typeof node !== 'object') return false;
	if (node.op === 'cmp') return onColumn(node, column);
	if (Array.isArray(node.args)) return node.args.some(arg => mentions(arg, column));
	if (node.arg) return mentions(node.arg, column);
	return false;
}

const scalarOf = (value) => {
	if (!value || (value.type !== 'str' && value.type !== 'num')) return undefined;
	return value.v;
};

/** A draft with nothing in it: what an unfiltered column's panel opens with. */
export function emptyDraft(kind) {
	if (PICKED_KINDS.has(kind)) return { values: [] };
	switch (kind) {
		case 'date': return emptyDateDraft();
		case 'text': return { op: 'contains', text: '' };
		case 'choice': return { values: [], empty: false };
		case 'range': return { min: undefined, max: undefined, empty: false };
		default: return { value: null };
	}
}

/**
 * The column's top-level conditions as the header's draft, or null when the
 * header has no control for that shape. `hits` are the conditions, in order.
 */
function draftOf(kind, hits, column, now) {
	const [first] = hits;
	if (kind === 'date') return readDateDraft(column, hits, now);
	if (PICKED_KINDS.has(kind)) return pickedDraftOf(kind, hits);
	if (kind === 'range') {
		if (hits.length === 1 && first.cmp === 'empty') return { min: undefined, max: undefined, empty: true };
		const draft = { min: undefined, max: undefined, empty: false };
		for (const hit of hits) {
			const raw = rawValueOf(hit.value);
			const plain = hit.value && hit.value.type !== 'list' && hit.value.type !== 'ref' && hit.value.type !== 'me';
			if (!plain || raw === undefined) return null;
			if (hit.cmp === 'gte' && draft.min === undefined) draft.min = raw;
			else if (hit.cmp === 'lte' && draft.max === undefined) draft.max = raw;
			else return null;
		}
		return draft;
	}
	if (hits.length !== 1) return null;
	const { cmp, value } = first;
	switch (kind) {
		case 'text': {
			if (isNullaryOperator(cmp)) return { op: cmp, text: '' };
			const text = scalarOf(value);
			return (cmp === 'contains' || cmp === 'eq') && text !== undefined ? { op: cmp, text: String(text) } : null;
		}
		case 'choice': {
			if (cmp === 'empty') return { values: [], empty: true };
			if (cmp === 'eq') {
				const one = scalarOf(value);
				return one === undefined ? null : { values: [String(one)], empty: false };
			}
			if (cmp === 'in' && value?.type === 'list') {
				const values = value.v.map(scalarOf);
				return values.every(entry => entry !== undefined) ? { values: values.map(String), empty: false } : null;
			}
			return null;
		}
		case 'boolean':
			if (cmp === 'empty') return { value: 'empty' };
			return cmp === 'eq' && value?.type === 'bool' ? { value: value.v } : null;
		case 'file':
		case 'entries':
			return cmp === 'empty' || cmp === 'nempty' ? { value: cmp } : null;
		default:
			return null;
	}
}

/**
 * Picked values (people, tags, items) as a draft: one `=` or one `in`, of
 * values a picker holds. `me` is a person's only; a lookup's are item ids (a
 * title typed in the builder is a title match, the builder's to show).
 */
function pickedDraftOf(kind, hits) {
	if (hits.length !== 1) return null;
	const [{ cmp, value }] = hits;
	const entries = cmp === 'eq' ? [value] : (cmp === 'in' && value?.type === 'list' ? value.v : null);
	if (!entries || !entries.length) return null;
	const values = [];
	for (const entry of entries) {
		if (entry?.type === 'me' && kind === 'user') values.push(ME_VALUE);
		else if (entry?.type === 'str' && (kind !== 'lookup' || isHexId(String(entry.v)))) values.push(String(entry.v));
		else return null;
	}
	return { values };
}

/**
 * What the column's header shows for `query`:
 *   `{ status, active, draft, writePath }`
 * - `status` 'none' (no condition on the column), 'set' (top-level conditions
 *   the header shows: `draft`), or 'builder' (the builder's to edit).
 * - `active`: the column has a top-level condition (the funnel fills).
 * - `writePath`: the spelling a write keeps — an id or an element group the
 *   query already names it by, else the column's own `#id`.
 * A column with no `kind` reads 'none' or 'builder' only.
 */
export function readColumnFilter(query, column, { now } = {}) {
	const text = (query || '').trim();
	const base = { status: 'none', active: false, draft: emptyDraft(column?.kind), writePath: column?.path || [] };
	if (!column || !text) return base;
	const { ast, error } = parseForBuilder(text);
	// Text that does not read cannot be shown, so it is never rewritten.
	if (error || !ast) return { ...base, status: 'builder' };
	const hits = [];
	let nested = false;
	topLevelConditionNodes(ast).forEach((node) => {
		if (node?.op === 'cmp') {
			if (onColumn(node, column)) hits.push(node);
		} else if (mentions(node, column)) nested = true;
	});
	const active = hits.length > 0;
	if (nested) return { ...base, status: 'builder', active };
	if (!active) return base;
	const spelled = hits.find(hit => hit.path.length === 1 && (isElementIdSegment(hit.path[0]) || isElementGroupSegment(hit.path[0])));
	const writePath = spelled ? [...spelled.path] : column.path;
	const draft = column.kind ? draftOf(column.kind, hits, column, now) : null;
	return draft ? { status: 'set', active, draft, writePath } : { ...base, status: 'builder', active, writePath };
}

const blank = raw => raw === undefined || raw === null || raw === '';

/**
 * The conditions a draft stands for, `[{ cmp, value? }]` — none for an empty
 * draft. `now` dates a period (Today, This month); the default is now.
 */
export function draftConditions(column, draft, now) {
	const dataType = column?.field?.dataType;
	if (!column?.kind || !draft) return [];
	const valued = (cmp, raw) => {
		const value = makeValueNode(dataType, cmp, raw);
		return value ? [{ cmp, value }] : [];
	};
	if (PICKED_KINDS.has(column.kind)) {
		const values = [...new Set((draft.values || []).filter(value => !blank(value)))];
		if (!values.length) return [];
		return values.length === 1 ? valued('eq', values[0]) : valued('in', values);
	}
	switch (column.kind) {
		case 'date':
			return dateDraftConditions(column, draft, now);
		case 'text':
			if (isNullaryOperator(draft.op)) return [{ cmp: draft.op }];
			// Spaces alone ask for nothing; anything else goes out as typed,
			// exactly as the builder's text box sends it.
			return String(draft.text ?? '').trim() ? valued(draft.op, draft.text) : [];
		case 'choice': {
			if (draft.empty) return [{ cmp: 'empty' }];
			const values = [...new Set((draft.values || []).filter(value => !blank(value)))];
			if (!values.length) return [];
			return values.length === 1 ? valued('eq', values[0]) : valued('in', values);
		}
		case 'boolean':
			if (draft.value === 'empty') return [{ cmp: 'empty' }];
			return typeof draft.value === 'boolean' ? valued('eq', draft.value) : [];
		case 'range':
			if (draft.empty) return [{ cmp: 'empty' }];
			return [
				...(blank(draft.min) ? [] : valued('gte', draft.min)),
				...(blank(draft.max) ? [] : valued('lte', draft.max)),
			];
		case 'file':
		case 'entries':
			return draft.value === 'empty' || draft.value === 'nempty' ? [{ cmp: draft.value }] : [];
		default:
			return [];
	}
}

/** What a header says when its condition would make the filter longer than a query may be. */
export const TOO_LONG_PROBLEM = 'That is more than one filter can hold. Pick fewer.';

/**
 * Why a draft cannot be applied, or null. A plain number range is checked
 * (a measurement's or an amount's ends compare in their units, which the
 * server reads), a custom range needs both days in order, and, given the
 * `query` it would be written into, nothing may make the filter longer than
 * the grammar reads (a hundred people picked, say).
 */
export function draftProblem(column, draft, { query, now } = {}) {
	const own = ownProblem(column, draft);
	if (own || query === undefined) return own;
	const next = composeColumnFilter(query, column, draft, now);
	return typeof next === 'string' && next.length > MAX_QUERY_LENGTH ? TOO_LONG_PROBLEM : null;
}

function ownProblem(column, draft) {
	if (column?.kind === 'date') return dateDraftProblem(draft);
	if (column?.kind !== 'range' || !draft || draft.empty || blank(draft.min) || blank(draft.max)) return null;
	const min = Number(draft.min);
	const max = Number(draft.max);
	if (typeof draft.min !== 'number' || typeof draft.max !== 'number' || !Number.isFinite(min) || !Number.isFinite(max)) return null;
	return min > max ? 'Min is above max.' : null;
}

/** The text a draft makes of `query`, or null when the header may not write it. */
function composeColumnFilter(query, column, draft, now) {
	const read = readColumnFilter(query, column, { now });
	if (!column?.kind || read.status === 'builder') return null;
	return applyConditionsToQuery(query, {
		path: read.writePath,
		forceElement: read.writePath === column.path ? column.forceElement : false,
		conditions: draftConditions(column, draft, now),
		replacePath: true,
		aliases: column.aliases,
	});
}

/**
 * `query` with the column's top-level condition replaced by the draft's (where
 * the first one stood), or removed when the draft asks for nothing. Canonical
 * text, or undefined for "no filter". A column the header cannot show is never
 * written, and neither is a draft with a problem (`draftProblem`): `query`
 * comes back as it was.
 */
export function writeColumnFilter(query, column, draft, { now } = {}) {
	if (draftProblem(column, draft, { query: query || '', now })) return query || undefined;
	const next = composeColumnFilter(query, column, draft, now);
	return next === null ? query || undefined : next;
}

/** `query` without the column's top-level condition (the header's Clear). Never touches one it cannot show. */
export function clearColumnFilter(query, column, { now } = {}) {
	const read = readColumnFilter(query, column, { now });
	if (!column || read.status === 'builder') return query || undefined;
	if (!read.active) return query || undefined;
	return clearConditionsOnPath(query, read.writePath, read.writePath === column.path ? column.forceElement : false, false, column.aliases);
}
